import {
  EntityKindFilter,
  EntityKindPicker,
  EntityTypeFilter,
  EntityTypePicker,
  EntityUserFilter,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import {
  EntityMembershipFilter,
  type TeamsEntityFilters,
} from './EntityMembershipFilter';

type ScopeValue = 'all' | 'mine' | 'starred';

const scopeOptions: { label: string; value: ScopeValue }[] = [
  { label: 'All', value: 'all' },
  { label: 'My Teams', value: 'mine' },
  { label: 'Starred', value: 'starred' },
];

const formatFilterValue = (value: string) =>
  value
    .split(/[-_]/g)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getFilterPanelSx = (theme: Theme) => {
  const tokens = getThemeTokens(theme);

  return {
    '& .TeamsExplorerFilters-grid': {
      display: 'grid',
      columnGap: 8,
      rowGap: 10,
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    '& .TeamsExplorerFilters-grid > *': {
      minWidth: 0,
      width: '100%',
    },
    '& [class*="BackstageSelect-root"]': {
      paddingBottom: '0 !important',
      paddingTop: '0 !important',
      width: '100%',
    },
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& [class*="BackstageSelect-formControl"]': {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      minWidth: 0,
      width: '100%',
    },
    '& [class*="BackstageSelect-formLabel"]': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.04em',
      lineHeight: 1.2,
      marginBottom: theme.spacing(0.5),
      position: 'static',
      textTransform: 'uppercase',
      transform: 'none',
    },
    '& [class*="MuiSelect-select"], & [role="button"]': {
      fontSize: 14,
    },
    '& [class*="BackstageSelectInputBase-root"]': {
      backgroundColor: getSurfaceColor(theme, 'subtle'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: `${radii.md}px`,
      boxShadow: 'none',
      marginTop: '0 !important',
      minHeight: 40,
    },
    '& [class*="BackstageSelectInputBase-root"] .MuiSelect-select': {
      alignItems: 'center',
      display: 'flex',
      minHeight: '32px !important',
      paddingLeft: '14px !important',
      paddingBottom: '0 !important',
      paddingRight: '38px !important',
      paddingTop: '0 !important',
    },
  };
};

const ScopeButton = ({
  active,
  disabled = false,
  label,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) => {
  const theme = useTheme();

  return (
    <ButtonBase
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      sx={{
        backgroundColor: active
          ? alpha(theme.palette.primary.main, 0.12)
          : getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${
          active
            ? alpha(theme.palette.primary.main, 0.32)
            : getSurfaceBorder(theme)
        }`,
        borderRadius: 999,
        color: active ? theme.palette.primary.main : theme.palette.text.primary,
        minHeight: 30,
        px: 1.25,
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.shorter },
        ),
      }}
    >
      <Typography
        sx={{ fontSize: 12.5, fontWeight: active ? 700 : 600, lineHeight: 1 }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
};

export const TeamsExplorerControls = ({
  searchText,
  setSearchText,
  userEntityRef,
}: {
  searchText: string;
  setSearchText: (value: string) => void;
  userEntityRef: string;
}) => {
  const theme = useTheme();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { filters, queryParameters, updateFilters } =
    useEntityList<TeamsEntityFilters>();
  const { starredEntities } = useStarredEntities();
  const didApplyDefaultRef = useRef(false);

  const typeValues = filters.type?.getTypes?.() ?? [];
  const selectedScope: ScopeValue = filters.membership
    ? 'mine'
    : (filters.user as { value?: string } | undefined)?.value === 'starred'
      ? 'starred'
      : 'all';

  // Enforce kind=group — prevents a stale ?filters[kind]=... URL param from showing
  // wrong-kind entities on a page where the kind picker is hidden.
  useEffect(() => {
    if (filters.kind?.value && filters.kind.value !== 'group') {
      updateFilters({ kind: new EntityKindFilter('group', 'Group') });
    }
  }, [filters.kind?.value, updateFilters]);

  // Rehydrate scope from URL on first load; default to showing all teams.
  useEffect(() => {
    if (didApplyDefaultRef.current) return;
    didApplyDefaultRef.current = true;

    if (queryParameters.membership) {
      const rawRef = Array.isArray(queryParameters.membership)
        ? queryParameters.membership[0]
        : queryParameters.membership;
      updateFilters({ membership: new EntityMembershipFilter(rawRef) });
      return;
    }
  }, [queryParameters.membership, updateFilters]);

  const setScope = (scope: ScopeValue) => {
    if (scope === 'mine') {
      updateFilters({
        user: EntityUserFilter.all(),
        membership: new EntityMembershipFilter(userEntityRef),
      });
      return;
    }
    if (scope === 'starred') {
      updateFilters({
        user: EntityUserFilter.starred(Array.from(starredEntities)),
        membership: undefined,
      });
      return;
    }
    updateFilters({
      user: EntityUserFilter.all(),
      membership: undefined,
    });
  };

  const clearAllFilters = () => {
    setSearchText('');
    setFiltersExpanded(false);
    updateFilters({
      kind: new EntityKindFilter('group', 'Group'),
      type: undefined,
      user: EntityUserFilter.all(),
      membership: undefined,
    });
  };

  const activeFilterChips = useMemo(
    () => [
      ...(selectedScope === 'mine'
        ? [
            {
              key: 'scope-mine',
              label: 'My Teams',
              onDelete: () => setScope('all'),
            },
          ]
        : []),
      ...(selectedScope === 'starred'
        ? [
            {
              key: 'scope-starred',
              label: 'Starred',
              onDelete: () => setScope('all'),
            },
          ]
        : []),
      ...typeValues.map(type => ({
        key: `type-${type}`,
        label: `Type: ${formatFilterValue(type)}`,
        onDelete: () => {
          const nextValues = typeValues.filter(value => value !== type);
          updateFilters({
            type: nextValues.length
              ? new EntityTypeFilter(nextValues)
              : undefined,
          });
        },
      })),
    ],
    [selectedScope, typeValues, updateFilters],
  );
  const activeFilterCount = activeFilterChips.length;
  const hasCollapsedFilterSummary =
    !filtersExpanded && activeFilterChips.length > 0;

  return (
    <Paper
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        px: { md: 2, xs: 1.5 },
        pb: hasCollapsedFilterSummary
          ? { md: 1, xs: 0.875 }
          : { md: 1.5, xs: 1.25 },
        pt: { md: 1.5, xs: 1.25 },
        ...getFilterPanelSx(theme),
      }}
    >
      <EntityKindPicker hidden initialFilter="group" />

      <Stack spacing={hasCollapsedFilterSummary ? 1 : 1.25}>
        <Stack
          alignItems={{ lg: 'center', xs: 'stretch' }}
          direction={{ lg: 'row', xs: 'column' }}
          spacing={1}
        >
          <OutlinedInput
            fullWidth
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            placeholder="Search teams"
            startAdornment={
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            }
            endAdornment={
              searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear team search"
                    edge="end"
                    onClick={() => setSearchText('')}
                    size="small"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              flex: 1,
              minHeight: 48,
              minWidth: 0,
            }}
          />

          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            justifyContent={{ lg: 'flex-end', xs: 'flex-start' }}
            spacing={0.75}
            useFlexGap
          >
            {scopeOptions.map(option => (
              <ScopeButton
                key={option.value}
                active={selectedScope === option.value}
                label={option.label}
                onClick={() => setScope(option.value)}
              />
            ))}

            <Button
              onClick={() => setFiltersExpanded(previous => !previous)}
              startIcon={<TuneOutlinedIcon />}
              size="small"
              variant={filtersExpanded ? 'contained' : 'outlined'}
            >
              {activeFilterCount > 0
                ? `Filters (${activeFilterCount})`
                : 'Filters'}
            </Button>
          </Stack>
        </Stack>

        {activeFilterChips.length > 0 && (
          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            spacing={hasCollapsedFilterSummary ? 0.5 : 0.75}
            useFlexGap
          >
            {activeFilterChips.map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={chip.onDelete}
                size="small"
                sx={{
                  backgroundColor: getSurfaceColor(theme, 'subtle'),
                  border: `1px solid ${getSurfaceBorder(theme)}`,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  height: 26,
                }}
              />
            ))}

            <Button
              onClick={clearAllFilters}
              size="small"
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              Clear all
            </Button>
          </Stack>
        )}

        <Collapse in={filtersExpanded}>
          <Stack spacing={1.25}>
            <Divider sx={{ borderColor: getSurfaceBorder(theme) }} />

            <Box className="TeamsExplorerFilters-grid">
              <Box sx={{ minWidth: 0 }}>
                <EntityTypePicker />
              </Box>
            </Box>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};
